// Fitness Plan Generator — creates structured weekly workout plans

export interface FitnessProfile {
  goal: string;
  level: string;
  days_per_week: number;
  equipment: string[];
}

interface ExerciseDef {
  name: string;
  sets: number;
  reps: string;
  muscle: string;
  equipment: string;
}

interface WorkoutDay {
  name: string;
  focus: string;
  exercises: ExerciseDef[];
}

const SETS_REPS: Record<string, { sets: number; reps: string }> = {
  muscle: { sets: 4, reps: "8-12" },
  strength: { sets: 5, reps: "3-6" },
  endurance: { sets: 3, reps: "15-20" },
  general: { sets: 3, reps: "10-12" },
  lose: { sets: 3, reps: "12-15" },
};

const PUSH_EXERCISES: ExerciseDef[] = [
  { name: "Bench Press", sets: 0, reps: "", muscle: "chest", equipment: "barbell" },
  { name: "Overhead Press", sets: 0, reps: "", muscle: "shoulders", equipment: "barbell" },
  { name: "Dumbbell Fly", sets: 0, reps: "", muscle: "chest", equipment: "dumbbell" },
  { name: "Lateral Raise", sets: 0, reps: "", muscle: "shoulders", equipment: "dumbbell" },
  { name: "Tricep Dip", sets: 0, reps: "", muscle: "triceps", equipment: "body weight" },
  { name: "Push Up", sets: 0, reps: "", muscle: "chest", equipment: "body weight" },
];

const PULL_EXERCISES: ExerciseDef[] = [
  { name: "Pull Up", sets: 0, reps: "", muscle: "back", equipment: "body weight" },
  { name: "Barbell Row", sets: 0, reps: "", muscle: "back", equipment: "barbell" },
  { name: "Dumbbell Curl", sets: 0, reps: "", muscle: "biceps", equipment: "dumbbell" },
  { name: "Face Pull", sets: 0, reps: "", muscle: "rear deltoids", equipment: "cable" },
  { name: "Deadlift", sets: 0, reps: "", muscle: "back", equipment: "barbell" },
];

const LEG_EXERCISES: ExerciseDef[] = [
  { name: "Squat", sets: 0, reps: "", muscle: "quads", equipment: "barbell" },
  { name: "Romanian Deadlift", sets: 0, reps: "", muscle: "hamstrings", equipment: "barbell" },
  { name: "Leg Press", sets: 0, reps: "", muscle: "quads", equipment: "leverage machine" },
  { name: "Lunges", sets: 0, reps: "", muscle: "quads", equipment: "body weight" },
  { name: "Leg Curl", sets: 0, reps: "", muscle: "hamstrings", equipment: "leverage machine" },
  { name: "Calf Raise", sets: 0, reps: "", muscle: "calves", equipment: "body weight" },
];

const UPPER_EXERCISES: ExerciseDef[] = [...PUSH_EXERCISES.slice(0, 3), ...PULL_EXERCISES.slice(0, 3)];
const LOWER_EXERCISES: ExerciseDef[] = [...LEG_EXERCISES];

const FULL_BODY: ExerciseDef[] = [
  { name: "Squat", sets: 0, reps: "", muscle: "quads", equipment: "barbell" },
  { name: "Bench Press", sets: 0, reps: "", muscle: "chest", equipment: "barbell" },
  { name: "Barbell Row", sets: 0, reps: "", muscle: "back", equipment: "barbell" },
  { name: "Overhead Press", sets: 0, reps: "", muscle: "shoulders", equipment: "barbell" },
  { name: "Dumbbell Curl", sets: 0, reps: "", muscle: "biceps", equipment: "dumbbell" },
  { name: "Plank", sets: 0, reps: "", muscle: "abs", equipment: "body weight" },
  { name: "Push Up", sets: 0, reps: "", muscle: "chest", equipment: "body weight" },
  { name: "Lunges", sets: 0, reps: "", muscle: "quads", equipment: "body weight" },
  { name: "Calf Raise", sets: 0, reps: "", muscle: "calves", equipment: "body weight" },
];

function applyGoal(exercises: ExerciseDef[], goal: string, count: number): ExerciseDef[] {
  const sr = SETS_REPS[goal] || SETS_REPS.general;
  return exercises.slice(0, count).map(e => ({ ...e, sets: sr.sets, reps: sr.reps }));
}

function filterByEquipment(exercises: ExerciseDef[], equipment: string[]): ExerciseDef[] {
  if (!equipment.length) return exercises;
  const allowed = new Set(["body weight"]);
  equipment.forEach(e => {
    if (e === "dumbbells") allowed.add("dumbbell");
    if (e === "barbell") allowed.add("barbell");
    if (e === "cables") { allowed.add("cable"); }
    if (e === "full_gym") { allowed.add("dumbbell"); allowed.add("barbell"); allowed.add("cable"); allowed.add("leverage machine"); }
    if (e === "home_gym") { allowed.add("dumbbell"); allowed.add("barbell"); }
    if (e === "bodyweight") allowed.add("body weight");
  });
  const filtered = exercises.filter(e => allowed.has(e.equipment));
  return filtered;
}

function buildSplit(days: number): { name: string; pool: ExerciseDef[] }[] {
  switch (days) {
    case 2: return [
      { name: "Full Body A", pool: FULL_BODY },
      { name: "Full Body B", pool: [...FULL_BODY].reverse() },
    ];
    case 3: return [
      { name: "Push", pool: PUSH_EXERCISES },
      { name: "Pull", pool: PULL_EXERCISES },
      { name: "Legs", pool: LEG_EXERCISES },
    ];
    case 4: return [
      { name: "Upper A", pool: UPPER_EXERCISES },
      { name: "Lower A", pool: LOWER_EXERCISES },
      { name: "Upper B", pool: [...UPPER_EXERCISES].reverse() },
      { name: "Lower B", pool: [...LOWER_EXERCISES].reverse() },
    ];
    case 5: return [
      { name: "Push", pool: PUSH_EXERCISES },
      { name: "Pull", pool: PULL_EXERCISES },
      { name: "Legs", pool: LEG_EXERCISES },
      { name: "Upper", pool: UPPER_EXERCISES },
      { name: "Lower", pool: LOWER_EXERCISES },
    ];
    case 6: return [
      { name: "Push A", pool: PUSH_EXERCISES },
      { name: "Pull A", pool: PULL_EXERCISES },
      { name: "Legs A", pool: LEG_EXERCISES },
      { name: "Push B", pool: [...PUSH_EXERCISES].reverse() },
      { name: "Pull B", pool: [...PULL_EXERCISES].reverse() },
      { name: "Legs B", pool: [...LEG_EXERCISES].reverse() },
    ];
    default: return buildSplit(3);
  }
}

export function generatePlan(profile: FitnessProfile) {
  const split = buildSplit(profile.days_per_week);
  const exPerDay = profile.level === "beginner" ? 4 : profile.level === "advanced" ? 6 : 5;
  const goalKey = profile.goal === "lose" ? "lose" : profile.goal === "muscle" ? "muscle" : profile.goal === "strength" ? "strength" : profile.goal === "endurance" ? "endurance" : "general";

  const days: WorkoutDay[] = split.map(s => ({
    name: s.name,
    focus: s.name.toLowerCase(),
    exercises: applyGoal(filterByEquipment(s.pool, profile.equipment), goalKey, exPerDay),
  }));

  const levelLabel = profile.level.charAt(0).toUpperCase() + profile.level.slice(1);
  const goalLabel = profile.goal.charAt(0).toUpperCase() + profile.goal.slice(1);

  return {
    name: `VYV ${goalLabel} — ${levelLabel}`,
    description: `${profile.days_per_week}-day ${split[0]?.name.includes("Full") ? "Full Body" : "Split"} program`,
    days,
    goal: profile.goal,
    level: profile.level,
    duration_weeks: 4,
    is_active: true,
    source: "vyv",
  };
}
